import { ref } from 'vue'
import { createFocusTrap } from 'focus-trap'

/**
 * @typedef CookiebotConsent
 * @prop {boolean} marketing
 * @prop {string|null} method
 * @prop {boolean} necessary
 * @prop {boolean} preferences
 * @prop {string|null} stamp
 * @prop {boolean} statistics
 * @description The Cookiebot consent object.
 */

function setupCookiebot() {
  const trap = ref()
  const necessary = ref(true)
  const marketing = ref(false)
  const preferences = ref(false)
  const statistics = ref(false)

  /**
   * Check if Cookiebot is loaded.
   */
  const loaded = () => {
    return typeof window !== 'undefined' && (window).Cookiebot;
  }

  /**
   * Update the consent and remove focus traps.
   */
  const updateAndClearTrap = () => {
    clearTrap()

    necessary.value = consent()?.necessary ?? true
    marketing.value = consent()?.marketing ?? false
    preferences.value = consent()?.preferences ?? false
    statistics.value = consent()?.statistics ?? false
  }

  /**
   * Renew/show the cookiebar.
   */
  const renew = () => {
    if (loaded()) {
      (window).Cookiebot.renew()
    } else {
      console.error('Cookiebot not loaded.');
    }
  }

  /**
   * Hide the cookiebar.
   */
  const hide = () => {
    if (loaded()) {
      (window).Cookiebot.hide()

      clearTrap()
    } else {
      console.error('Cookiebot not loaded.');
    }
  }

  /**
   * Set a (soft) focus trap for the created cookiebar. Outside clicks
   * are allowed, but the keyboards stays in the bar.
   */
  const setTrap = () => {
    clearTrap()

    trap.value = createFocusTrap(['#CybotCookiebotDialog'], {
      allowOutsideClick: true,
    }).activate()
  }

  /**
   * Clear an existing focus trap.
   */
  const clearTrap = () => {
    if (trap.value) {
      trap.value.deactivate()
    }
  }

  /**
   * Retrieve the entered consent when available.
   * @returns {CookiebotConsent|null}
   */
  const consent = () => {
    return loaded()
      ? (window).Cookiebot.consent
      : null
  }

  /**
   * Setup listeners.
   */
  window.addEventListener('CookiebotOnConsentReady', updateAndClearTrap)
  window.addEventListener('CookiebotOnDialogDisplay', setTrap)

  return {
    loaded,
    renew,
    hide,
    necessary,
    marketing,
    preferences,
    statistics,
  }
}

const instance = setupCookiebot()

export function useCookiebot() {
  return instance
}
